"use client";

import { Mail, Navigation, Phone } from "lucide-react";
import { useLanguage } from "./LanguageProvider";

/** Bilspektrum on Formvägen according to OpenStreetMap. */
const LOCATION = { lat: 63.8505964, lon: 20.3107809 };
const BBOX = [LOCATION.lon - 0.012, LOCATION.lat - 0.0045, LOCATION.lon + 0.012, LOCATION.lat + 0.0045].join(",");
// OpenStreetMap embed: interactive, no cookies, so no consent banner needed (unlike Google Maps).
const MAP_SRC = `https://www.openstreetmap.org/export/embed.html?bbox=${BBOX}&layer=mapnik&marker=${LOCATION.lat},${LOCATION.lon}`;
// Google Maps picks its language from the visitor's Google account/browser, so pass the site language with hl.
const directionsUrl = (lang: string) =>
  `https://www.google.com/maps/dir/?api=1&destination=Bilspektrum+AB,+Formv%C3%A4gen+10+A,+Ume%C3%A5&hl=${lang}`;

type Props = {
  /** Size of the map box, e.g. "aspect-[4/3] lg:aspect-[5/6]" or "h-[60vh]". */
  className?: string;
  /** Show e-mail in the info card as well. */
  showEmail?: boolean;
};

/** Interactive map of the dealership with an address card and a directions button. */
export function DealershipMap({ className = "aspect-[4/3]", showEmail = false }: Props) {
  const { language } = useLanguage();
  const sv = language === "sv";

  return (
    <div className={`relative overflow-hidden rounded-lg bg-neutral-200 ring-1 ring-black/5 ${className}`}>
      <iframe
        src={MAP_SRC}
        title={sv ? "Karta: Bilspektrum AB, Formvägen 10 A, Umeå" : "Map: Bilspektrum AB, Formvägen 10 A, Umeå"}
        loading="lazy"
        className="absolute inset-0 h-full w-full border-0"
      />
      <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center justify-between gap-3 rounded-lg bg-white/95 p-4 shadow-lg backdrop-blur sm:right-auto sm:gap-6">
        <div className="space-y-0.5 text-sm">
          <p className="font-semibold text-foreground">Bilspektrum AB</p>
          <p className="text-muted-foreground">Formvägen 10 A, Umeå</p>
          <a href="tel:090-120400" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
            <Phone className="h-3.5 w-3.5" aria-hidden />
            090-12 04 00
          </a>
          {showEmail ? (
            <a href="mailto:info@bilspektrum.se" className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
              <Mail className="h-3.5 w-3.5" aria-hidden />
              info@bilspektrum.se
            </a>
          ) : null}
        </div>
        <a
          href={directionsUrl(sv ? "sv" : "en")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
        >
          <Navigation className="h-4 w-4" aria-hidden />
          {sv ? "Vägbeskrivning" : "Directions"}
        </a>
      </div>
    </div>
  );
}
